import { useState, useEffect, useRef } from "react";
import { listAllJobs, deleteAnyJob } from "../../lib/api";
import type { Job } from "../../lib/types";
import { ProgressBar } from "../../components/ProgressBar";
import { Icon } from "../../components/icons";

const STATUS_COLORS: Record<string, string> = {
  queued: "text-yellow-400",
  processing: "text-blue-400",
  complete: "text-emerald-400",
  failed: "text-red-400",
  cancelled: "text-slate-500",
};

export function AllJobs() {
  const [jobs, setJobs] = useState<Job[]>([]);
  const [loading, setLoading] = useState(true);
  const [cursor, setCursor] = useState<string | undefined>();
  const [error, setError] = useState<string | null>(null);
  const [playingId, setPlayingId] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement>(null);

  useEffect(() => {
    loadJobs();
  }, []);

  async function loadJobs() {
    try {
      const result = await listAllJobs(50);
      setJobs(result.items);
      setCursor(result.cursor);
    } catch {
      setError("Failed to load samples");
    } finally {
      setLoading(false);
    }
  }

  const handleLoadMore = async () => {
    if (!cursor) return;
    try {
      const result = await listAllJobs(50, cursor);
      setJobs((prev) => [...prev, ...result.items]);
      setCursor(result.cursor);
    } catch {
      setError("Failed to load more");
    }
  };

  const handleDelete = async (job: Job) => {
    if (confirmDeleteId !== job.jobId) {
      setConfirmDeleteId(job.jobId);
      return;
    }
    setConfirmDeleteId(null);
    try {
      await deleteAnyJob(job.jobId, job.userId);
      setJobs((prev) => prev.filter((j) => j.jobId !== job.jobId));
    } catch {
      setError("Failed to delete");
    }
  };

  const handlePlay = (job: Job) => {
    if (!audioRef.current || !job.audioUrl) return;
    if (playingId === job.jobId) {
      audioRef.current.pause();
      setPlayingId(null);
    } else {
      audioRef.current.src = job.audioUrl;
      audioRef.current.play();
      setPlayingId(job.jobId);
    }
  };

  return (
    <div className="content">
      <div className="ph">
        <div>
          <div className="ph-eyebrow">Admin · Samples</div>
          <h1 className="ph-title">All samples</h1>
          <div className="ph-lede">
            Workspace-wide history. {jobs.length} loaded.
          </div>
        </div>
        <div className="ph-actions">
          <button type="button" className="btn btn-ghost" onClick={loadJobs}>
            <Icon.Refresh /> Refresh
          </button>
        </div>
      </div>

      {error && <div className="err-banner">{error}</div>}

      <audio ref={audioRef} onEnded={() => setPlayingId(null)} preload="none" />

      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="card animate-pulse h-24" />
          ))}
        </div>
      ) : (
        <div className="space-y-2">
          {jobs.map((job) => (
            <div key={job.jobId} className="card space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span>{job.type === "tts" ? "🎙" : "🔄"}</span>
                  <span className="text-xs font-mono text-slate-500">
                    {job.jobId.slice(0, 8)}…
                  </span>
                  <span className={`text-xs font-medium ${STATUS_COLORS[job.status]}`}>
                    {job.status}
                  </span>
                </div>
                <span className="text-xs text-slate-600">
                  {new Date(job.createdAt).toLocaleString()}
                </span>
              </div>

              {/* Metadata */}
              <div className="text-xs text-slate-500 space-y-0.5">
                <p>User: <span className="text-slate-400 font-mono">{job.userId.slice(0, 12)}…</span></p>
                <p>Voice: <span className="text-slate-400">{job.referenceName}</span></p>
                {job.text && (
                  <p className="line-clamp-1">Text: <span className="text-slate-400">"{job.text}"</span></p>
                )}
                {job.deviceType && (
                  <p>Device: <span className="text-slate-400">{job.deviceType}</span></p>
                )}
                {job.genTimeSeconds && (
                  <p>Gen time: <span className="text-slate-400">{job.genTimeSeconds.toFixed(1)}s</span></p>
                )}
                {job.gpuType && (
                  <p>GPU: <span className="text-slate-400">{job.gpuType}</span></p>
                )}
                {job.inferenceSeconds != null && (
                  <p>Inference: <span className="text-slate-400">{job.inferenceSeconds.toFixed(2)}s</span></p>
                )}
                {job.totalJobCost != null && (
                  <p>Cost: <span className="text-emerald-400 font-medium">${job.totalJobCost.toFixed(4)}</span></p>
                )}
              </div>

              {/* Progress bar for active */}
              {(job.status === "queued" || job.status === "processing") && (
                <ProgressBar progress={job.progress} />
              )}

              {/* Actions */}
              <div className="flex gap-2 justify-end">
                {job.status === "complete" && job.audioUrl && (
                  <button
                    onClick={() => handlePlay(job)}
                    className="btn-ghost text-xs"
                  >
                    {playingId === job.jobId ? "⏸ Pause" : "▶ Play"}
                  </button>
                )}
                <button
                  onClick={() => handleDelete(job)}
                  onBlur={() => setConfirmDeleteId(null)}
                  className={`text-xs ${confirmDeleteId === job.jobId ? "btn-danger ring-2 ring-red-400" : "btn-danger"}`}
                >
                  {confirmDeleteId === job.jobId ? "Confirm?" : "🗑 Delete"}
                </button>
              </div>
            </div>
          ))}

          {jobs.length === 0 && (
            <p className="text-sm text-slate-500 text-center py-8">No samples found</p>
          )}
        </div>
      )}

      {cursor && (
        <button onClick={handleLoadMore} className="btn-ghost w-full">
          Load more
        </button>
      )}
    </div>
  );
}
